import { useState } from "react"
import { Button, FormField, Select, TextField } from "@smooth/design-system-components"
import {
  DUE_DAY_CHOICES,
  PLAN_LIMITS,
  PLAN_STATUS_LABELS,
  applyPlan,
  initialPlan,
  planErrors,
} from "../installmentPlan"
import type { InstallmentPlan, PlanStatus } from "../installmentPlan"
import type { Scenario } from "../types"

// 欄の誤りは押したときに欄ごとに出す（押せない灰色ボタンにしない）

const OTHER_DAY = "other"
const dueDayLabel = (day: number) => (day === 31 ? "末日" : `${day}日`)
const DUE_DAY_OPTIONS = [
  ...DUE_DAY_CHOICES.map((day) => ({ value: String(day), label: dueDayLabel(day) })),
  { value: OTHER_DAY, label: "日にちを入れる" },
]

/** Number("") は 0 になるので、空欄も NaN にして誤りとして出す */
const numberOf = (value: string) => (value.trim() === "" ? Number.NaN : Number(value))
const shown = (value: number) => (Number.isNaN(value) ? "" : value)

type Props = {
  scenario: Scenario
  contractIndex: number
  busy: boolean
  onChange: (patch: Partial<Scenario>) => void
  defaultOpen?: boolean
}

export function InstallmentPlanForm({
  scenario,
  contractIndex,
  busy,
  onChange,
  defaultOpen = false,
}: Props) {
  const contract = scenario.contracts?.[contractIndex]
  const [plan, setPlan] = useState<InstallmentPlan>(() =>
    initialPlan(contract, scenario.asOf, scenario.nextDueOn)
  )
  const [customDay, setCustomDay] = useState(
    () => !(DUE_DAY_CHOICES as readonly number[]).includes(plan.dueDay)
  )
  const [showErrors, setShowErrors] = useState(false)
  const errors = showErrors ? planErrors(plan) : {}
  const edit = (patch: Partial<InstallmentPlan>) => setPlan((current) => ({ ...current, ...patch }))
  const where = `契約${contractIndex + 1}`
  const hasRows = (contract?.installments.length ?? 0) > 0

  const build = () => {
    if (Object.keys(planErrors(plan)).length > 0) {
      setShowErrors(true)
      return
    }
    setShowErrors(false)
    onChange(applyPlan(scenario, contractIndex, plan))
  }

  return (
    <details
      open={defaultOpen || undefined}
      aria-label={`分割の予定をまとめて作る ${where}`}
      className="@container rounded-md border border-border-default px-3 py-2"
    >
      <summary className="cursor-pointer text-body font-bold text-text-default">
        分割の予定をまとめて作る
      </summary>
      <div className="grid gap-4 pt-3 @[480px]:grid-cols-2">
        <FormField label="分割回数" error={errors.count}>
          <TextField
            aria-label={`分割回数（まとめて作る） ${where}`}
            type="number"
            min={1}
            max={PLAN_LIMITS.count}
            value={shown(plan.count)}
            onChange={(e) => edit({ count: numberOf(e.target.value) })}
          />
        </FormField>
        <FormField label="金額の決め方">
          <Select
            aria-label={`金額の決め方（まとめて作る） ${where}`}
            value={plan.amountMode}
            onChange={(e) => edit({ amountMode: e.target.value as InstallmentPlan["amountMode"] })}
            options={[
              { value: "monthly", label: "月額で決める" },
              { value: "total", label: "総額で決める（端数は初回）" },
            ]}
          />
        </FormField>
        <FormField
          label={plan.amountMode === "total" ? "総額（円）" : "月額（円）"}
          error={errors.amount}
        >
          <TextField
            aria-label={`金額（まとめて作る） ${where}`}
            type="number"
            min={1}
            max={PLAN_LIMITS.amount}
            value={shown(plan.amount)}
            onChange={(e) => edit({ amount: numberOf(e.target.value) })}
          />
        </FormField>
        <FormField label="毎月の約定日" error={customDay ? undefined : errors.dueDay}>
          <Select
            aria-label={`毎月の約定日（まとめて作る） ${where}`}
            value={customDay ? OTHER_DAY : String(plan.dueDay)}
            onChange={(e) => {
              const other = e.target.value === OTHER_DAY
              setCustomDay(other)
              if (!other) edit({ dueDay: Number(e.target.value) })
            }}
            options={DUE_DAY_OPTIONS}
          />
        </FormField>
        {customDay && (
          <FormField label="約定日の日にち" error={errors.dueDay}>
            <TextField
              aria-label={`約定日の日にち（まとめて作る） ${where}`}
              type="number"
              min={1}
              max={31}
              value={shown(plan.dueDay)}
              onChange={(e) => edit({ dueDay: numberOf(e.target.value) })}
            />
          </FormField>
        )}
        <FormField label="初回の月" error={errors.firstMonth}>
          <TextField
            aria-label={`初回の月（まとめて作る） ${where}`}
            type="month"
            value={plan.firstMonth}
            onChange={(e) => edit({ firstMonth: e.target.value })}
          />
        </FormField>
        <FormField label="入金・遅延">
          <Select
            aria-label={`入金・遅延（まとめて作る） ${where}`}
            value={plan.status}
            onChange={(e) => edit({ status: e.target.value as PlanStatus })}
            options={(Object.keys(PLAN_STATUS_LABELS) as PlanStatus[]).map((status) => ({
              value: status,
              label: PLAN_STATUS_LABELS[status],
            }))}
          />
        </FormField>
        {plan.status === "from" && (
          <FormField label="遅延が始まる回" error={errors.overdueFrom}>
            <TextField
              aria-label={`遅延が始まる回（まとめて作る） ${where}`}
              type="number"
              min={1}
              max={PLAN_LIMITS.count}
              value={shown(plan.overdueFrom)}
              onChange={(e) => edit({ overdueFrom: numberOf(e.target.value) })}
            />
          </FormField>
        )}
      </div>
      <div className="pb-1 pt-3">
        <Button variant="outline" disabled={busy} onClick={build}>
          {hasRows ? "分割の予定を作り直す" : "分割の予定を作る"}
        </Button>
      </div>
    </details>
  )
}
